import { arrayOf, bool, number, oneOf, shape, string } from 'prop-types'

import {
  PLAN_CHANGE_KIND, PLAN_CHANGE_TIMING, PLAN_TYPE, TIER,
} from './constants'

export const planShape = shape({
  planType: oneOf([PLAN_TYPE.YEARLY, PLAN_TYPE.MONTHLY]).isRequired,
  priceCents: number.isRequired,
  currency: string.isRequired,
})

export const plansShape = arrayOf(planShape)

const tierPlanShape = shape({
  monthlyPrice: number.isRequired,
  annualPrice: number.isRequired,
  downloadsPerMonth: number.isRequired,
  generationsPerMonth: number.isRequired,
})

export const catalogValuesShape = shape({
  [TIER.ESSENTIALS]: string,
  [TIER.PREMIUM]: string,
})

export const availablePlansShape = shape({
  [TIER.ESSENTIALS]: tierPlanShape.isRequired,
  [TIER.PREMIUM]: tierPlanShape.isRequired,
  premiumCheckoutAvailable: bool.isRequired,
})

export const trackingDataShape = shape({
  tier: string,
  planType: string,
  daysSinceSubStart: number,
  downloadsUsed: number,
  billingTerm: string,
})

export const allowanceShape = shape({
  state: string,
  monthlyAllowance: number.isRequired,
  downloadsAvailable: number.isRequired,
  generationWeight: number.isRequired,
  generationsAvailable: number.isRequired,
  remaining: number,
  resetsAt: string,
  endsAt: string,
})

const TERMS = [PLAN_TYPE.MONTHLY, PLAN_TYPE.ANNUAL]

export const scheduledChangeShape = shape({
  target: string.isRequired,
  kind: oneOf([PLAN_CHANGE_KIND.DOWNGRADE, PLAN_CHANGE_KIND.TO_MONTHLY]).isRequired,
  tier: oneOf([TIER.ESSENTIALS, TIER.PREMIUM]).isRequired,
  billingTerm: oneOf(TERMS).isRequired,
  price: number.isRequired,
  effectiveAt: string.isRequired,
})

export const planChangeTargetShape = shape({
  target: string.isRequired,
  tier: oneOf([TIER.ESSENTIALS, TIER.PREMIUM]).isRequired,
  billingTerm: oneOf(TERMS).isRequired,
  kind: oneOf(Object.values(PLAN_CHANGE_KIND)),
  timing: oneOf(Object.values(PLAN_CHANGE_TIMING)),
  available: bool.isRequired,
  blockedReason: string,
})

export const planChangesShape = shape({
  blockedReason: string,
  effectiveAt: string,
  targets: arrayOf(planChangeTargetShape).isRequired,
})

export const pauseOfferShape = shape({
  available: bool.isRequired,
  blockedReason: string,
  startsAt: string,
  resumesAt: string,
})

export const subscriptionShape = shape({
  id: number,
  billingCycle: oneOf([PLAN_TYPE.MONTHLY, PLAN_TYPE.YEARLY]),
  status: string,
  statusLabel: string,
  price: string,
  renewsOn: string,
  periodEndDate: string,
  paymentMethod: string,
  downloadsUsed: number,
  downloadLimit: number,
  canceledAt: string,
  canceledByAdmin: bool,
  endedOn: string,
  updatePaymentUrl: string,
  planType: oneOf([PLAN_TYPE.MONTHLY, PLAN_TYPE.YEARLY]),
  tier: string,
  gracePeriodEligible: bool,
  commitmentReleased: bool,
  scheduledPlanSwitchTo: string,
  scheduledPlanSwitchAt: string,
  pausedAt: string,
  pausedUntil: string,
  userPauseScheduledAt: string,
  userPause: bool,
  daysSinceSubStart: number,
  billingTerm: oneOf([PLAN_TYPE.MONTHLY, PLAN_TYPE.ANNUAL]),
  termPrice: number,
  nextBillingDate: string,
  downloadedCount: number,
  downloadedValue: number,
  allowance: allowanceShape,
  scheduledChange: scheduledChangeShape,
  planChanges: planChangesShape,
  pauseOffer: pauseOfferShape,
  userPauseScheduledUntil: string,
})

export const paymentHistoryShape = arrayOf(
  shape({
    id: number,
    amountPaid: string,
    taxAmount: string,
    currency: string,
    paymentDate: string,
    status: string,
    statusLabel: string,
    invoiceUrl: string,
    paymentMethod: string,
    billingPeriod: string,
  })
)

export const subscriptionPromoSectionShape = shape({
  images: shape({
    wave: string.isRequired,
    waveLarge: string.isRequired,
    butterfly: string.isRequired,
    bird: string.isRequired,
    flower: string.isRequired,
  }).isRequired,
  locale: string,
  ctaUrl: string,
  subscriptionCatalogValue: string,
  subscriptionFlowEnabled: bool,
  isActiveSubscriber: bool,
  showPremiumPlans: bool,
})
